// ═══════════════════════════════════════════════════════════════
// V·IAnet Governance — AdminNivelesPuesto (migrado al shell · prompt 0471 M6)
// CRUD table para niveles de puesto, usa shared/components/admin/
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchNivelesPuesto,
  actualizarNivelPuesto,
  eliminarNivelPuesto,
  type NivelPuesto,
} from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import { NivelModal } from './niveles-puesto-panel/NivelModal';
import {
  AdminPageHeader,
  AdminTableShell,
  AdminActionButton,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

// ─── Icons SVG inline ─────────────────────────────────────────

function EditIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#2F5496"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    </svg>
  );
}

// ─── Component ───────────────────────────────────────────────

export function AdminNivelesPuesto() {
  const { t } = useI18n();
  const adm = t.structure.admin;
  const admRec = adm as unknown as Record<string, unknown>;
  const tooltipsRec = (admRec.tooltips as Record<string, unknown> | undefined) ?? {};

  const [niveles, setNiveles] = useState<NivelPuesto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<NivelPuesto | 'new' | null>(null);
  const [toast, setToast] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    fetchNivelesPuesto()
      .then((r) => setNiveles(r.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const moveOrder = async (nivel: NivelPuesto, direction: 'up' | 'down') => {
    const idx = niveles.findIndex((n) => n.id === nivel.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= niveles.length) return;
    const other = niveles[swapIdx];
    await Promise.all([
      actualizarNivelPuesto(nivel.id, { orden: other.orden }),
      actualizarNivelPuesto(other.id, { orden: nivel.orden }),
    ]);
    load();
  };

  const handleDelete = async (nivel: NivelPuesto) => {
    const confirmMsg = ((admRec.confirmDeleteLevel as string | undefined) ??
      `¿Eliminar el nivel "${nivel.nombre}"?`) as string;
    if (!window.confirm(confirmMsg)) return;
    try {
      await eliminarNivelPuesto(nivel.id);
      load();
      showToast(adm.saved ?? 'Eliminado');
    } catch {
      showToast(adm.error ?? 'Error al eliminar');
    }
  };

  // Filtro cliente por nombre / código / nombre corto
  const filteredNiveles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return niveles;
    return niveles.filter(
      (n) =>
        n.nombre.toLowerCase().includes(q) ||
        n.codigo.toLowerCase().includes(q) ||
        (n.nombreCorto ?? '').toLowerCase().includes(q),
    );
  }, [niveles, search]);

  // Para reordenar se necesita el índice en el array ordenado por `orden` (mismo
  // que `niveles` — el backend ya lo devuelve ordenado). El filtro cliente no
  // afecta la lógica de swap: moveOrder usa `niveles` original.
  const columns: AdminTableColumn<NivelPuesto>[] = [
    {
      key: 'orden',
      label: (admRec.orden as string | undefined) ?? 'Orden',
      align: 'center',
      width: 70,
      sortable: true,
      sortKey: (n) => n.orden,
    },
    {
      key: 'codigo',
      label: adm.colCodigo,
      mono: true,
      sortable: true,
      sortKey: (n) => n.codigo,
    },
    {
      key: 'nombre',
      label: adm.colNombre,
      sortable: true,
      sortKey: (n) => n.nombre,
    },
    {
      key: 'nombreCorto',
      label: (admRec.nombreCorto as string | undefined) ?? 'Nombre corto',
      render: (n) => <span>{n.nombreCorto ?? '—'}</span>,
    },
    {
      key: 'color',
      label: (admRec.color as string | undefined) ?? 'Color',
      render: (n) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: n.color ?? '#ccc',
              border: '1px solid #D1D5DB',
              display: 'inline-block',
            }}
          />
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: n.colorFondo ?? '#eee',
              border: '1px solid #D1D5DB',
              display: 'inline-block',
            }}
          />
          <span style={{ fontSize: 11, color: '#6B7280' }}>{n.color ?? '—'}</span>
        </div>
      ),
    },
    {
      key: 'activo',
      label: adm.activo,
      align: 'center',
      render: (n) => (
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            background: n.activo ? '#ECFDF5' : '#FEF2F2',
            color: n.activo ? '#16A34A' : '#DC2626',
          }}
        >
          {n.activo ? (adm.activo ?? 'Sí') : (adm.inactivo ?? 'No')}
        </span>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={adm.levels ?? 'Niveles de puesto'}
        infoTooltip={{
          title: (admRec.levels as string | undefined) ?? 'Niveles de puesto',
          text:
            (tooltipsRec.adminNivelesPuesto as { ayuda?: string } | undefined)?.ayuda ??
            'Catálogo de niveles jerárquicos de puesto (Junior, Senior, Manager, Director, etc.).\n\nCada puesto se asigna a un nivel. Los niveles determinan la escala retributiva, la complejidad de las responsabilidades y la ruta de carrera.\n\nUsa las flechas ↑↓ para reordenar los niveles por jerarquía. El orden refleja la progresión profesional dentro de la organización.',
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: adm.search,
        }}
        onNew={{
          label: adm.newLevel,
          onClick: () => setModal('new'),
        }}
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: '#0E243E',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      <AdminTableShell
        columns={columns}
        rows={filteredNiveles}
        rowKey={(n) => n.id}
        onRowClick={(n) => setModal(n)}
        loading={loading}
        loadingMessage={(t.common as unknown as Record<string, string>).cargando ?? 'Cargando…'}
        emptyMessage={adm.noResults}
        actions={(n) => {
          const idx = niveles.findIndex((x) => x.id === n.id);
          const isFirst = idx === 0;
          const isLast = idx === niveles.length - 1;
          return (
            <>
              <AdminActionButton
                icon={<EditIcon />}
                label={(tooltipsRec.editar as string | undefined) ?? 'Editar'}
                onClick={() => setModal(n)}
              />
              <AdminActionButton
                icon={<ArrowUpIcon />}
                label={(admRec.moveUp as string | undefined) ?? 'Subir'}
                onClick={() => moveOrder(n, 'up')}
                disabled={isFirst}
              />
              <AdminActionButton
                icon={<ArrowDownIcon />}
                label={(admRec.moveDown as string | undefined) ?? 'Bajar'}
                onClick={() => moveOrder(n, 'down')}
                disabled={isLast}
              />
              <AdminActionButton
                icon={<TrashIcon />}
                label={(tooltipsRec.eliminar as string | undefined) ?? 'Eliminar'}
                onClick={() => handleDelete(n)}
                variant="danger"
              />
            </>
          );
        }}
      />

      {modal !== null && (
        <NivelModal
          nivel={modal === 'new' ? null : modal}
          maxOrden={niveles.length > 0 ? Math.max(...niveles.map((n) => n.orden)) : 0}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
            showToast(adm.saved ?? 'Guardado');
          }}
        />
      )}
    </div>
  );
}
